/**
 * @module Internet
 * @description Internet Page
 */
import Head from "next/head";
import Tooltip from "@/blocks/Tooltip";
import Link from "next/link";
import { trackEvent } from "@/js/helpers/tracking";

// Template
import Template from "@/templates/Default";

// Blocks
import HeroAddressCheck from "@/blocks/Heroes/HeroAddressCheck";
import BannerList from "@/blocks/Banners/BannerList";
import SplitSectionCopy from "@/blocks/SplitSection/SplitSectionCopy";
import ColumnContent from "@/blocks/ColumnContent";
import FAQs from "@/blocks/FAQS/InternetFAQS";

// Cohesion
import MonarchFallback from "@/blocks/MonarchFallback";
import { MonarchSlot } from "@redventures/cohesion-utils-react";

/**
 * @function Page
 * @description Page
 */
const Page = () => {
  /**
   * @function handleClick
   * @description
   */
  const handleClick = (event) => {
    const { target } = event;
    const text = target.innerHTML;
    const data = {
      elementType: "LINK",
      location: "INTERNET",
      position: "SPLIT SECTION",
      text,
      actionOutcome: "INTERNALLINK",
    };

    trackEvent({ action: "elementClicked", data, event });
  };

  return (
    <>
      <Head>
        <title>Frontier Home Internet Service: Wi-Fi Plans & Pricing</title>
        <meta
          name="description"
          content="Experience fast and reliable Frontier home internet plans with powerful Wi-Fi for seamless streaming, gaming, and work. Sign up for home internet today!"
        />
        <link rel="canonical" href="https://frontier.com/shop/internet" />
      </Head>

      <Template
        title="Get Internet Through High Speed or Fiber Internet Services Today"
        hero={
          <>
            {/* Internet | Above Hero */}
            <MonarchSlot slotId="frdcInternetAboveHero" />

            <HeroAddressCheck
              useSelectAreasLegal={
                <>
                  Frontier<sup>&reg;</sup> Fiber Internet available in
                  select&nbsp;areas.
                </>
              }
              headline={
                <>
                  <span key="span-1" className="u-color-blue">
                    Connect
                  </span>{" "}
                  your world
                </>
              }
            />

            {/* Internet | Below Hero */}
            <MonarchSlot slotId="frdcInternetBelowHero" />
          </>
        }
        primary={
          <>
            <BannerList
              theme="blue"
              items={[
                "Internet your way",
                "No data caps",
                <>
                  No overage charges
                  <Tooltip disclosure="data-caps" />
                </>,
                <>
                  Free installation
                  <Tooltip disclosure="includes-tech-installation" />
                </>,
              ]}
            />

            <SplitSectionCopy
              title="Internet for doing"
              content={[
                {
                  headline: (
                    <>
                      <Link
                        href="/shop/internet/fiber-internet"
                        style={{ textDecoration: "underline" }}
                        onClick={handleClick}
                      >
                        Frontier<sup key="sup-1">&reg;</sup> Fiber Internet
                      </Link>
                    </>
                  ),
                  copy: "Do everything fiber fast. Work, live and play how you want with the power of our 100% fiber-optic network.",
                },
                {
                  headline: (
                    <Link
                      href="/shop"
                      style={{ textDecoration: "underline" }}
                      onClick={handleClick}
                    >
                      Frontier Internet
                    </Link>
                  ),
                  copy: (
                    <>
                      Connect to the people and things you love most with
                      confidence. No data caps or overage fees.
                      <Tooltip disclosure="data-caps" />
                    </>
                  ),
                },
              ]}
              image={{
                url: "/pages/internet/mother-and-son-checking-frontier-internet-service.jpg",
                blurUrl: "/pages/home/split-section-720x514.jpg",
                alt: "Mother and Son Checking Frontier Internet Service",
              }}
              subtitle={false}
            />

            <ColumnContent
              theme="dark"
              useTiles
              headline="Why Frontier?"
              subhead="We’re here to make internet easy"
              items={[
                {
                  icon: "tv-lines",
                  headline: "Free installation",
                  content: (
                    <>
                      We make setup easy with free installation available for
                      every fiber order.
                      <Tooltip disclosure="includes-tech-installation" />
                    </>
                  ),
                },
                {
                  icon: "speed-guage",
                  headline: "No data caps",
                  content: (
                    <>
                      Unlock everything the internet has to offer with unlimited
                      data and no overage charges.
                      <Tooltip disclosure="data-caps" />
                    </>
                  ),
                },
                {
                  icon: "account",
                  headline: "24/7 service",
                  content:
                    "Our expert technical support team is available day and night.",
                },
              ]}
            />

            <MonarchSlot
              placement="frdcInternetSection3"
              fallback={<MonarchFallback height={["0px", "0px", "0px"]} />}
            />

            <FAQs />
          </>
        }
      />
    </>
  );
};

export default Page;
